import { timeToMinutes } from '@/utils/datetime/time';
import type { BusinessHoursDto } from '@/services/api/petseo.client.wrapper';

export type BusinessHoursFit = 'unknown' | 'inside' | 'outside' | 'closed';

interface OpenWindow {
  start: number;
  end: number;
}

const MINUTES_IN_DAY = 24 * 60;

export const isBusinessHoursMissing = (businessHours: BusinessHoursDto[] | undefined): boolean => {
  if (!businessHours) return false;
  return businessHours.length === 0 || businessHours.every((h) => !h.isActive);
};

const getOpenWindowsForDay = (businessHours: BusinessHoursDto[], dayOfWeek: number): OpenWindow[] => {
  const windows = businessHours
    .filter((h) => h.isActive && h.dayOfWeek === dayOfWeek)
    .map((h) =>
      h.is24Hours ? { start: 0, end: MINUTES_IN_DAY } : { start: timeToMinutes(h.startTime ?? '00:00'), end: timeToMinutes(h.endTime ?? '00:00') }
    )
    .sort((a, b) => a.start - b.start);

  return windows.reduce<OpenWindow[]>((merged, window) => {
    const previous = merged.at(-1);

    if (previous && window.start <= previous.end) {
      previous.end = Math.max(previous.end, window.end);
      return merged;
    }

    return [...merged, { ...window }];
  }, []);
};

export const getBusinessHoursFit = (
  businessHours: BusinessHoursDto[] | undefined,
  dayOfWeek: number,
  startTime: string,
  endTime: string
): BusinessHoursFit => {
  if (!businessHours || isBusinessHoursMissing(businessHours)) return 'unknown';

  const windows = getOpenWindowsForDay(businessHours, dayOfWeek);
  if (windows.length === 0) return 'closed';

  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);

  return windows.some((w) => w.start <= start && end <= w.end) ? 'inside' : 'outside';
};

export const isOutsideBusinessHours = (
  businessHours: BusinessHoursDto[] | undefined,
  dayOfWeek: number,
  startTime: string,
  endTime: string
): boolean => {
  const fit = getBusinessHoursFit(businessHours, dayOfWeek, startTime, endTime);
  return fit === 'outside' || fit === 'closed';
};
